/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { useState } from 'react';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';

import ButtonItem from '@atlaskit/menu/button-item';
import { token } from '@atlaskit/tokens';
import mergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { JiraEntryPointContainer } from '@jira/platform__entry-points-container/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import { useEntryPointButtonTrigger } from '@jira/platform__entry-point-button-trigger/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { Skeleton } from '../skeleton/index.tsx';
import type { ChangeViewMenuItemProps } from './types.tsx';
import { ProjectsViewOptionsEntrypoint } from './projects-view-options/ProjectsViewOptionsEntrypoint.entrypoint.tsx';

const changeViewMenuItemLabel = defineMessage({
	id: 'navigation-apps-sidebar-nav4-display-settings.change-view-action.change-view-menu-item-label',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Change view',
	description: 'Used as the button label text for the "Change view" menu item',
});

const listBulletedIcon = defineMessage({
	id: 'navigation-apps-sidebar-nav4-display-settings.change-view-action.list-bulleted-icon',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'List bulleted icon',
	description: 'The label for the list bulleted icon',
});

const chevronRightIcon = defineMessage({
	id: 'navigation-apps-sidebar-nav4-display-settings.change-view-action.chevron-right-icon',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Chevron right icon',
	description: 'The label for the chevron right icon',
});

export function ChangeViewMenuItem({
	onOpen,
	onChange,
}: ChangeViewMenuItemProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const cloudId = useCloudId();

	const onChangeViewMenuItemClick = () => {
		if (!isOpen && onOpen) {
			onOpen();
		}
		setIsOpen(!isOpen);
	};
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		ProjectsViewOptionsEntrypoint,
		{
			cloudId,
			onChange,
		},
	);

	const entrypointRef = useEntryPointButtonTrigger(entryPointActions);

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			content={() => (
				<JiraEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id="display-settings-menu-popup-content"
					packageName="jira-navigation-apps-sidebar-nav4-display-settings"
					fallback={<Skeleton />}
					runtimeProps={{}}
					errorFallback="flag"
				/>
			)}
			placement="right-start"
			// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
			messageId="navigation-apps-sidebar-nav4-display-settings.ui.change-view-menu-item.popup"
			messageType="transactional"
			shouldRenderToParent
			trigger={({ ref, ...triggerProps }) => (
				<ButtonItem
					{...triggerProps}
					ref={mergeRefs(ref, entrypointRef)}
					isSelected={isOpen}
					iconBefore={
						<ListBulletedIcon
							label={formatMessage(listBulletedIcon)}
							spacing="spacious"
							color={token('color.icon')}
						/>
					}
					iconAfter={<ChevronRightIcon label={formatMessage(chevronRightIcon)} size="small" />}
					onClick={onChangeViewMenuItemClick}
					aria-label={formatMessage(changeViewMenuItemLabel)}
					aria-expanded={isOpen}
					aria-haspopup="true"
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					testId="navigation-apps-sidebar-nav4-display-settings.ui.change-view-menu-item.button"
				>
					{formatMessage(changeViewMenuItemLabel)}
				</ButtonItem>
			)}
		/>
	);
}
